// When a package lands, in words — t-159 §4/§5 ("in 6 days", "Mon, Sep 15,
// 00:00 UTC", "scheduled by … on Sep 8, 14:22 UTC") and §7.
//
// Its own module because the banner and the two dialogs all print these, and a
// file that exports both components and helpers cannot be hot-replaced.

import type { Cycle } from "./types";

/**
 * The moment a package waiting on this board is applied, and which kind of
 * moment it is: the close of the open cycle, or — between two windows of a
 * recurring_event board — the open of the cycle already scheduled.
 */
export interface NextCut {
  at: string;
  kind: "closes" | "opens";
}

/** Read off the board's newest cycle, so every tab names the same moment. A
   scheduled cycle is a pause only on recurring_event; elsewhere it is an event
   that has not started, and the package still waits for its close. */
export function nextCutOf(
  windowKind: string,
  newest: Cycle | undefined,
): NextCut | undefined {
  if (newest?.status === "open" && newest.close_at) {
    return { at: newest.close_at, kind: "closes" };
  }
  if (windowKind === "recurring_event" && newest?.status === "scheduled") {
    return { at: newest.open_at, kind: "opens" };
  }
  return undefined;
}

/** "the open cycle closes" / "the next cycle opens" — the event a package
   waits for, defaulting to a close where the cycles are not known yet. */
export function cutEvent(cut: NextCut | undefined): string {
  return cut?.kind === "opens" ? "the next cycle opens" : "the open cycle closes";
}

const MOMENT_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

const STAMP_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

/** "Mon, Sep 15, 00:00 UTC", or null for a date there is none of — the
   sentences around it are rewritten when it is missing, never padded. */
export function formatMoment(iso: string | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return `${MOMENT_FMT.format(t)} UTC`;
}

/** "Sep 8, 14:22 UTC" — the shorter stamp, for something already past. */
export function formatStamp(iso: string | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return `${STAMP_FMT.format(t)} UTC`;
}

/**
 * "in 6 days" — how far off the cut is, in the largest unit that still says
 * something useful. Null when the date is unknown or already behind us: a
 * package whose moment has passed is being applied right now, and counting
 * backwards from it would read as a countdown that overshot.
 */
export function formatUntil(iso: string | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const ms = t - now;
  if (ms <= 0) return null;
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return minutes <= 1 ? "in a minute" : `in ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return hours === 1 ? "in an hour" : `in ${hours} hours`;
  const days = Math.round(hours / 24);
  return `in ${days} days`;
}

/** "Mon, Sep 15" — the save bar's own short form of the next cut. Short on
   purpose: the bar is one line and the year is never the question there. The
   Settings form spells this out for itself; the Rewards tab reads it here. */
const SHORT_CUT_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export function formatShortCut(iso: string | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return SHORT_CUT_FMT.format(t);
}
